import { useState } from "react";
import { useSm, type CustomKind, type CustomValues } from "./view";

/** What a kind counts as for the appliesTo of features. */
const featureTarget: Record<CustomKind, string | null> = { weapon: "waffe", armor: "ruestung", shield: "schild", item: null };

type Feature = { feature: string; level: string };
type Minimum = { attribute: string; value: string };

const text = (value: unknown) => (value == null ? "" : String(value));
/** A whole number as typed, also with the typographic minus; empty means no value. */
const normalized = (value: string) => value.trim().replace(/−/g, "-");
const isInteger = (value: string) => normalized(value) === "" || /^-?\d+$/.test(normalized(value));
const integer = (value: string) => (normalized(value) === "" ? null : Number(normalized(value)));
const notAnInteger = "Bitte eine ganze Zahl angeben.";

function features(value: unknown): Feature[] {
  return Array.isArray(value) ? value.map((f: { feature?: string; level?: number | null }) => ({ feature: f.feature ?? "", level: text(f.level) })) : [];
}

function minimums(value: unknown): Minimum[] {
  return value && typeof value === "object" ? Object.entries(value as Record<string, number>).map(([attribute, v]) => ({ attribute, value: String(v) })) : [];
}

/**
 * The values of a weapon, armour, shield or item the user makes himself, in the fields of the rule data. The form only collects them;
 * the engine checks them and asks for the approval of the game master (konzept-gegenstaende E3).
 */
export function CustomValuesForm(props: { kind: CustomKind; initial?: CustomValues; submitLabel: string; busy?: boolean; onSubmit: (values: CustomValues) => void }) {
  const { view } = useSm();
  const form = view.customForm;
  const initial = props.initial ?? {};
  const profile = ((initial.profiles as Record<string, unknown>[] | undefined) ?? [])[0] ?? {};
  const [name, setName] = useState(text(initial.name));
  const [price, setPrice] = useState(text(initial.price));
  const [load, setLoad] = useState(text(initial.load).replace(".", ","));
  const [hardness, setHardness] = useState(text(initial.hardness));
  const [availability, setAvailability] = useState(text(initial.availability));
  const [complexity, setComplexity] = useState(text(initial.complexity));
  const [category, setCategory] = useState(text(initial.category));
  const [defense, setDefense] = useState(text(initial.defense));
  const [reduction, setReduction] = useState(text(initial.damageReduction));
  const [handicap, setHandicap] = useState(text(initial.handicap));
  const [tick, setTick] = useState(text(initial.tickPenalty));
  const [minStrength, setMinStrength] = useState(text(initial.minStrength));
  const [weaponKind, setWeaponKind] = useState(text(profile.kind) || (form.weaponKinds[0]?.id ?? ""));
  const [skill, setSkill] = useState(text(profile.skill) || (form.weaponSkills[0]?.id ?? ""));
  const [attributes, setAttributes] = useState<string[]>(() => {
    const given = (profile.attributes as string[] | undefined) ?? [];
    return Array.from({ length: form.weaponAttributes }, (_, i) => given[i] ?? view.attributes[i]?.id ?? "");
  });
  const [damage, setDamage] = useState(text(profile.damage));
  const [speed, setSpeed] = useState(text(profile.speed));
  const [range, setRange] = useState(text(profile.range));
  const [ammoPrice, setAmmoPrice] = useState(text((initial.ammunition as { price?: unknown } | undefined)?.price));
  const [ownFeatures, setOwnFeatures] = useState<Feature[]>(() => features(props.kind === "weapon" ? profile.features : initial.features));
  const [ownMinimums, setOwnMinimums] = useState<Minimum[]>(() => minimums(props.kind === "weapon" ? profile.minAttributes : initial.minAttributes));
  const target = featureTarget[props.kind];
  const offered = form.features.filter((f) => target != null && (f.appliesTo.length === 0 || f.appliesTo.includes(target)));

  const numbers =
    props.kind === "weapon"
      ? [speed, range]
      : props.kind === "armor"
        ? [defense, reduction, handicap, tick, minStrength]
        : props.kind === "shield"
          ? [defense, handicap, tick]
          : [];
  const unreadable =
    [hardness, ...numbers].some((v) => !isInteger(v)) ||
    (target != null && ownFeatures.some((f) => !isInteger(f.level))) ||
    ((props.kind === "weapon" || props.kind === "shield") && ownMinimums.some((m) => !isInteger(m.value)));

  const values = (): CustomValues => {
    const featureList = ownFeatures.filter((f) => f.feature).map((f) => ({ feature: f.feature, ...(f.level.trim() ? { level: integer(f.level) } : {}) }));
    const minimumMap = Object.fromEntries(ownMinimums.filter((m) => m.attribute && m.value.trim()).map((m) => [m.attribute, integer(m.value)]));
    const common: CustomValues = { name: name.trim(), price, load, hardness, availability, complexity };
    switch (props.kind) {
      case "weapon":
        return {
          ...common,
          profiles: [
            { kind: weaponKind, skill, attributes, minAttributes: minimumMap, damage: damage.trim().toUpperCase(), speed: integer(speed), ...(range.trim() ? { range: integer(range) } : {}), features: featureList },
          ],
          ...(ammoPrice.trim() ? { ammunition: { price: ammoPrice } } : {}),
        };
      case "armor":
        return { ...common, defense: integer(defense), damageReduction: integer(reduction), handicap: integer(handicap), tickPenalty: integer(tick), minStrength: integer(minStrength), features: featureList };
      case "shield":
        return { ...common, defense: integer(defense), handicap: integer(handicap), tickPenalty: integer(tick), minAttributes: minimumMap, features: featureList };
      default:
        return { ...common, category };
    }
  };

  const number = (label: string, value: string, set: (v: string) => void) => (
    <label className="field">
      <span>{label}</span>
      <input inputMode="numeric" value={value} aria-invalid={!isInteger(value)} onChange={(e) => set(e.target.value)} />
      {!isInteger(value) && <span className="error small">{notAnInteger}</span>}
    </label>
  );
  const select = (label: string, value: string, set: (v: string) => void, options: { id: string; name: string }[], empty?: string) => (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(e) => set(e.target.value)}>
        {empty !== undefined && <option value="">{empty}</option>}
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="stack small-gap" aria-label="Eigene Werte">
      <label className="field">
        <span>Name</span>
        <input autoFocus value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      {props.kind === "weapon" && (
        <>
          <div className="inline-form two">
            {select("Kampfart", weaponKind, setWeaponKind, form.weaponKinds)}
            {select("Fertigkeit", skill, setSkill, form.weaponSkills)}
          </div>
          <div className="inline-form two">
            {attributes.map((a, i) => (
              <label key={i} className="field">
                <span>Attribut {i + 1}</span>
                <select value={a} onChange={(e) => setAttributes(attributes.map((x, j) => (j === i ? e.target.value : x)))}>
                  {view.attributes.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
          <div className="inline-form two">
            <label className="field">
              <span>Schaden</span>
              <input value={damage} placeholder="z. B. 1W6+3" onChange={(e) => setDamage(e.target.value)} />
            </label>
            {number("WGS", speed, setSpeed)}
            {number("Reichweite (optional)", range, setRange)}
            <label className="field">
              <span>Munition je Stück (nur Schusswaffen)</span>
              <input value={ammoPrice} placeholder="z. B. 5 T" onChange={(e) => setAmmoPrice(e.target.value)} />
            </label>
          </div>
        </>
      )}
      {(props.kind === "armor" || props.kind === "shield") && (
        <div className="inline-form two">
          {number("VTD-Bonus", defense, setDefense)}
          {props.kind === "armor" && number("Schadensreduktion", reduction, setReduction)}
          {number("Behinderung", handicap, setHandicap)}
          {number("Tick-Zuschlag", tick, setTick)}
          {props.kind === "armor" && number("Mindeststärke (optional)", minStrength, setMinStrength)}
        </div>
      )}
      {props.kind === "item" && select("Kategorie", category, setCategory, view.itemCategories, "ohne Kategorie")}
      {(props.kind === "weapon" || props.kind === "shield") && (
        <fieldset className="stack small-gap">
          <legend>Mindestattribute</legend>
          {ownMinimums.map((m, i) => (
            <div key={i} className="inline-form two">
              <select aria-label={`Mindestattribut ${i + 1}`} value={m.attribute} onChange={(e) => setOwnMinimums(ownMinimums.map((x, j) => (j === i ? { ...x, attribute: e.target.value } : x)))}>
                {view.attributes.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <input aria-label={`Mindestwert ${i + 1}`} inputMode="numeric" value={m.value} aria-invalid={!isInteger(m.value)} onChange={(e) => setOwnMinimums(ownMinimums.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <button type="button" className="button secondary" onClick={() => setOwnMinimums(ownMinimums.filter((_, j) => j !== i))}>
                Entfernen
              </button>
            </div>
          ))}
          <button type="button" className="button secondary" onClick={() => setOwnMinimums([...ownMinimums, { attribute: view.attributes[0]?.id ?? "", value: "" }])}>
            + Mindestattribut
          </button>
        </fieldset>
      )}
      {target != null && (
        <fieldset className="stack small-gap">
          <legend>Merkmale</legend>
          {ownFeatures.map((f, i) => {
            const def = form.features.find((x) => x.id === f.feature);
            return (
              <div key={i} className="inline-form two">
                <select aria-label={`Merkmal ${i + 1}`} value={f.feature} onChange={(e) => setOwnFeatures(ownFeatures.map((x, j) => (j === i ? { ...x, feature: e.target.value } : x)))}>
                  {offered.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                {def?.leveled && (
                  <input aria-label={`Stufe ${def.name}`} inputMode="numeric" placeholder="Stufe" value={f.level} aria-invalid={!isInteger(f.level)} onChange={(e) => setOwnFeatures(ownFeatures.map((x, j) => (j === i ? { ...x, level: e.target.value } : x)))} />
                )}
                <button type="button" className="button secondary" onClick={() => setOwnFeatures(ownFeatures.filter((_, j) => j !== i))}>
                  Entfernen
                </button>
              </div>
            );
          })}
          <button type="button" className="button secondary" disabled={offered.length === 0} onClick={() => setOwnFeatures([...ownFeatures, { feature: offered[0]?.id ?? "", level: "" }])}>
            + Merkmal
          </button>
        </fieldset>
      )}
      <div className="inline-form two">
        <label className="field">
          <span>Preis</span>
          <input value={price} placeholder="z. B. 12 L" onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label className="field">
          <span>Last</span>
          <input inputMode="decimal" value={load} placeholder="z. B. 1 oder 0,5" onChange={(e) => setLoad(e.target.value)} />
        </label>
        {number("Härte", hardness, setHardness)}
        {select("Verfügbarkeit", availability, setAvailability, form.availability, "ohne Angabe")}
        {select("Komplexität", complexity, setComplexity, form.complexity, "ohne Angabe")}
      </div>
      <p className="muted small">Ein Eigenbau mit Werten ist mit der Spielleitung abzustimmen; der Bogen zeigt dafür einen Hinweis zum Quittieren, nach jeder Änderung der Werte erneut.</p>
      {unreadable && (
        <p className="error small" role="alert">
          Die markierten Felder brauchen eine ganze Zahl.
        </p>
      )}
      <div className="actions">
        <button type="button" className="button primary" disabled={!name.trim() || (props.kind === "weapon" && !damage.trim()) || unreadable || props.busy} onClick={() => props.onSubmit(values())}>
          {props.submitLabel}
        </button>
      </div>
    </div>
  );
}
